import * as React from 'react';
import { z } from 'zod/v4';
import { useTranslation } from 'react-i18next';
import {
  DynamicForm,
  Input,
  TextArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  MultipleSelector,
  DateTimeField,
  Button,
  AnnouncementPreview,
  ANNOUNCEMENT_STYLE_NAMES,
  type AnnouncementPreviewViewport,
  type AnnouncementStyleName,
  type Option,
} from '@org/ui';
import { Loader2 } from 'lucide-react';

// Stable reference to avoid re-creating default arrays on every render,
// which would cascade through useMemo deps and trigger a form reset.
const EMPTY_OPTIONS: Option[] = [];

/** What a brand-new announcement looks like unless the admin picks otherwise. */
const DEFAULT_NEW_STYLE: AnnouncementStyleName = 'wall-note';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Values emitted by the form on submit. `groupIds`/`userIds` are emitted as
 * `string[]` so the consuming routes can pass them straight to the mutations.
 * Empty optional text fields are emitted as `null` (the update mutation uses
 * `null` to clear a field; the create route converts `null` to `undefined`).
 */
export type AnnouncementFormValues = {
  title: string;
  body: string | null;
  mediaType: 'none' | 'image' | 'video' | 'animation';
  mediaUrl: string | null;
  posterUrl: string | null;
  ctaLabel: string | null;
  ctaTo: string | null;
  targetType: 'all' | 'e2e' | 'groups' | 'users';
  status: 'draft' | 'active' | 'closed';
  skippable: boolean;
  startsAt: Date | null;
  endsAt: Date | null;
  priority: number;
  style: AnnouncementStyleName;
  groupIds: string[];
  userIds: string[];
};

export type AnnouncementFormDefaultValues = {
  title?: string;
  body?: string;
  mediaType?: 'none' | 'image' | 'video' | 'animation';
  mediaUrl?: string;
  posterUrl?: string;
  ctaLabel?: string;
  ctaTo?: string;
  targetType?: 'all' | 'e2e' | 'groups' | 'users';
  status?: 'draft' | 'active' | 'closed';
  skippable?: boolean;
  /** Local datetime string in `yyyy-MM-ddTHH:mm` format, or '' when unset. */
  startsAt?: string;
  /** Local datetime string in `yyyy-MM-ddTHH:mm` format, or '' when unset. */
  endsAt?: string;
  priority?: number;
  /** Pop-up look; edit mode passes `classic` for announcements saved without one. */
  style?: AnnouncementStyleName;
};

export type AnnouncementFormProps = {
  defaultValues?: AnnouncementFormDefaultValues;
  onSubmit: (values: AnnouncementFormValues) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  /** Async search for groups (3+ chars, debounced by MultipleSelector). */
  onSearchGroups?: (query: string) => Promise<Option[]>;
  /** Async search for users (3+ chars, debounced by MultipleSelector). */
  onSearchUsers?: (query: string) => Promise<Option[]>;
  /** Pre-selected group options for edit mode (displayed as badges on load). */
  defaultSelectedGroups?: Option[];
  /** Pre-selected user options for edit mode (displayed as badges on load). */
  defaultSelectedUsers?: Option[];
  /** Show the "closed" option in the status select (edit mode). */
  includeClosedStatus?: boolean;
  labels?: {
    title?: string;
    body?: string;
    mediaType?: string;
    mediaUrl?: string;
    posterUrl?: string;
    ctaLabel?: string;
    ctaTo?: string;
    targetType?: string;
    status?: string;
    skippable?: string;
    startsAt?: string;
    endsAt?: string;
    priority?: string;
    style?: string;
    mediaNone?: string;
    mediaImage?: string;
    mediaVideo?: string;
    mediaAnimation?: string;
    targetAll?: string;
    targetE2e?: string;
    targetGroups?: string;
    targetUsers?: string;
    statusDraft?: string;
    statusActive?: string;
    statusClosed?: string;
    skippableOn?: string;
    skippableOff?: string;
    groups?: string;
    users?: string;
    groupSearchPlaceholder?: string;
    userSearchPlaceholder?: string;
    datePlaceholder?: string;
  };
};

// ---------------------------------------------------------------------------
// Zod schema  (used internally by DynamicForm)
// ---------------------------------------------------------------------------

const announcementSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  body: z.string().optional().default(''),
  mediaType: z.enum(['none', 'image', 'video', 'animation']).default('none'),
  mediaUrl: z.string().optional().default(''),
  posterUrl: z.string().optional().default(''),
  ctaLabel: z.string().optional().default(''),
  ctaTo: z.string().optional().default(''),
  style: z.enum(ANNOUNCEMENT_STYLE_NAMES).default(DEFAULT_NEW_STYLE),
  targetType: z.enum(['all', 'e2e', 'groups', 'users']).default('all'),
  // groupIds and userIds are stored as Option[] inside the form
  // (MultipleSelector) and converted back to string[] on submit.
  groupIds: z.array(z.any()).default([]),
  userIds: z.array(z.any()).default([]),
  status: z.enum(['draft', 'active', 'closed']).default('draft'),
  skippable: z.boolean().default(true),
  // Stored as datetime-local strings; converted to Date on submit.
  startsAt: z.string().optional().default(''),
  endsAt: z.string().optional().default(''),
  priority: z.coerce.number().int().default(0),
});

type SchemaValues = z.infer<typeof announcementSchema>;

type RendererContext = {
  field: { value: unknown; onChange: (value: unknown) => void };
  error?: string;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AsyncMultiSelect({
  value,
  onChange,
  onSearch,
  placeholder,
  emptyLabel,
}: {
  value: Option[];
  onChange: (options: Option[]) => void;
  onSearch?: (query: string) => Promise<Option[]>;
  placeholder: string;
  emptyLabel: string;
}) {
  return (
    <MultipleSelector
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      delay={400}
      placeholder={placeholder}
      emptyIndicator={
        <p className="text-center text-sm text-muted-foreground">
          {emptyLabel}
        </p>
      }
      loadingIndicator={
        <div className="flex items-center justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      }
      hidePlaceholderWhenSelected
    />
  );
}

/**
 * Live preview beside the form: the same cards the student app renders, fed
 * with whatever is typed so far (placeholders while title/body are empty).
 */
function AnnouncementLivePreview({ values }: { values: Partial<SchemaValues> }) {
  const { t, i18n } = useTranslation('admin');
  const [viewport, setViewport] = React.useState<AnnouncementPreviewViewport>('phone');

  const style = values.style ?? DEFAULT_NEW_STYLE;
  const mediaType = values.mediaType ?? 'none';
  const title = values.title?.trim() || t('announcements.form.preview.placeholderTitle');
  const body = values.body?.trim() || t('announcements.form.preview.placeholderBody');

  return (
    <aside className="flex flex-col gap-3 lg:sticky lg:top-0">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{t('announcements.form.preview.title')}</h3>
        <div className="flex gap-1" role="group" aria-label={t('announcements.form.preview.title')}>
          {(['phone', 'desktop'] as const).map((option) => (
            <Button
              key={option}
              type="button"
              size="sm"
              variant={viewport === option ? 'default' : 'outline'}
              className="h-7 px-2 text-xs"
              onClick={() => setViewport(option)}
            >
              {t(`announcements.form.preview.${option}`)}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex justify-center overflow-hidden rounded-xl border bg-muted/30 p-2">
        <AnnouncementPreview
          style={style}
          viewport={viewport}
          title={title}
          body={body}
          mediaType={mediaType}
          mediaUrl={mediaType !== 'none' ? values.mediaUrl?.trim() || null : null}
          posterUrl={mediaType === 'video' ? values.posterUrl?.trim() || null : null}
          primaryLabel={values.ctaLabel?.trim() || null}
          skippable={values.skippable ?? true}
          locale={i18n.language?.startsWith('pt') ? 'pt-BR' : 'en-US'}
          animationPlaceholder={t('announcements.form.preview.animationPlaceholder')}
        />
      </div>

      <p className="text-xs text-muted-foreground">{t('announcements.form.preview.note')}</p>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AnnouncementForm({
  defaultValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  onSearchGroups,
  onSearchUsers,
  defaultSelectedGroups = EMPTY_OPTIONS,
  defaultSelectedUsers = EMPTY_OPTIONS,
  includeClosedStatus = false,
  labels,
}: AnnouncementFormProps) {
  const { t } = useTranslation('admin');
  const formRef = React.useRef<HTMLFormElement>(null);
  const [liveValues, setLiveValues] = React.useState<Partial<SchemaValues>>({});

  // -- Compute DynamicForm defaultValues ------------------------------------
  const dynamicDefaultValues = React.useMemo<Partial<SchemaValues>>(
    () => ({
      title: defaultValues?.title ?? '',
      body: defaultValues?.body ?? '',
      mediaType: defaultValues?.mediaType ?? 'none',
      mediaUrl: defaultValues?.mediaUrl ?? '',
      posterUrl: defaultValues?.posterUrl ?? '',
      ctaLabel: defaultValues?.ctaLabel ?? '',
      ctaTo: defaultValues?.ctaTo ?? '',
      style: defaultValues?.style ?? DEFAULT_NEW_STYLE,
      targetType: defaultValues?.targetType ?? 'all',
      groupIds: defaultSelectedGroups as any,
      userIds: defaultSelectedUsers as any,
      status: defaultValues?.status ?? 'draft',
      skippable: defaultValues?.skippable ?? true,
      startsAt: defaultValues?.startsAt ?? '',
      endsAt: defaultValues?.endsAt ?? '',
      priority: defaultValues?.priority ?? 0,
    }),
    [defaultValues, defaultSelectedGroups, defaultSelectedUsers],
  );

  // -- Handle submit: convert form state to mutation-friendly values --------
  const handleSubmit = async (data: SchemaValues) => {
    const groupIds = (data.groupIds as unknown as Option[])
      .map((o) => o.value)
      .filter(Boolean);
    const userIds = (data.userIds as unknown as Option[])
      .map((o) => o.value)
      .filter(Boolean);

    const emptyToNull = (value: string | undefined) => {
      const trimmed = value?.trim();
      return trimmed ? trimmed : null;
    };

    onSubmit({
      title: data.title,
      body: emptyToNull(data.body),
      mediaType: data.mediaType,
      mediaUrl: data.mediaType !== 'none' ? emptyToNull(data.mediaUrl) : null,
      posterUrl: data.mediaType === 'video' ? emptyToNull(data.posterUrl) : null,
      ctaLabel: emptyToNull(data.ctaLabel),
      ctaTo: emptyToNull(data.ctaTo),
      targetType: data.targetType,
      status: data.status,
      skippable: data.skippable,
      startsAt: data.startsAt ? new Date(data.startsAt) : null,
      endsAt: data.endsAt ? new Date(data.endsAt) : null,
      priority: data.priority ?? 0,
      style: data.style,
      groupIds: data.targetType === 'groups' ? groupIds : [],
      userIds: data.targetType === 'users' ? userIds : [],
    });
  };

  // -- Custom renderers -----------------------------------------------------

  const customRenderers: Record<string, (context: RendererContext) => React.ReactNode> = {
    // --- Body textarea ---
    body: ({ field }) => (
      <TextArea
        value={(field.value ?? '') as string}
        onChange={field.onChange}
        className="min-h-[120px]"
        disabled={isSubmitting}
      />
    ),

    // --- Media type select ---
    mediaType: ({ field }) => (
      <Select
        value={field.value as string}
        onValueChange={(v) => field.onChange(v)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{labels?.mediaNone ?? 'None'}</SelectItem>
          <SelectItem value="image">{labels?.mediaImage ?? 'Image'}</SelectItem>
          <SelectItem value="video">{labels?.mediaVideo ?? 'Video'}</SelectItem>
          <SelectItem value="animation">{labels?.mediaAnimation ?? 'Animation (native)'}</SelectItem>
        </SelectContent>
      </Select>
    ),

    // --- Visual style select (what the pop-up looks like in the student app) ---
    style: ({ field }) => {
      const value = (field.value as AnnouncementStyleName | undefined) ?? DEFAULT_NEW_STYLE;
      return (
        <div className="space-y-1">
          <Select value={value} onValueChange={(v) => field.onChange(v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANNOUNCEMENT_STYLE_NAMES.map((name) => (
                <SelectItem key={name} value={name}>
                  {t(`announcements.list.styleLabels.${name}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t(`announcements.form.styleHints.${value}`)}</p>
        </div>
      );
    },

    // --- Target type select ---
    targetType: ({ field }) => (
      <Select
        value={field.value as string}
        onValueChange={(v) => field.onChange(v)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{labels?.targetAll ?? 'All students'}</SelectItem>
          <SelectItem value="e2e">{labels?.targetE2e ?? 'E2E students (group + private)'}</SelectItem>
          <SelectItem value="groups">{labels?.targetGroups ?? 'Specific groups'}</SelectItem>
          <SelectItem value="users">{labels?.targetUsers ?? 'Specific users'}</SelectItem>
        </SelectContent>
      </Select>
    ),

    // --- Groups multi-select with async search ---
    groupIds: ({ field }) => (
      <AsyncMultiSelect
        value={(field.value ?? []) as Option[]}
        onChange={(options) => field.onChange(options)}
        onSearch={onSearchGroups}
        placeholder={labels?.groupSearchPlaceholder ?? 'Search groups (3+ chars)...'}
        emptyLabel="No groups found"
      />
    ),

    // --- Users multi-select with async search ---
    userIds: ({ field }) => (
      <AsyncMultiSelect
        value={(field.value ?? []) as Option[]}
        onChange={(options) => field.onChange(options)}
        onSearch={onSearchUsers}
        placeholder={labels?.userSearchPlaceholder ?? 'Search students (3+ chars)...'}
        emptyLabel="No students found"
      />
    ),

    // --- Status select ---
    status: ({ field }) => (
      <Select
        value={field.value as string}
        onValueChange={(v) => field.onChange(v)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="draft">{labels?.statusDraft ?? 'Draft'}</SelectItem>
          <SelectItem value="active">{labels?.statusActive ?? 'Active'}</SelectItem>
          {includeClosedStatus && (
            <SelectItem value="closed">{labels?.statusClosed ?? 'Closed'}</SelectItem>
          )}
        </SelectContent>
      </Select>
    ),

    // --- Skippable toggle ---
    skippable: ({ field }) => (
      <div className="flex items-center gap-3">
        <Switch
          checked={field.value as boolean}
          onCheckedChange={(checked) => field.onChange(checked)}
        />
        <span className="text-sm text-muted-foreground">
          {field.value
            ? labels?.skippableOn ?? 'Students can dismiss without watching'
            : labels?.skippableOff ?? 'Students must complete before closing'}
        </span>
      </div>
    ),

    // --- Schedule window: the shared date+time field (same shadcn calendar as
    // every other date in the admin), kept as `yyyy-MM-ddTHH:mm` strings. No
    // label prop: DynamicForm already renders the field label. A date picked
    // without a time means "from the start of that day".
    startsAt: ({ field }) => (
      <DateTimeField
        value={((field.value ?? '') as string) || undefined}
        onChange={(next) => field.onChange(next ?? '')}
        placeholder={labels?.datePlaceholder}
        disabled={isSubmitting}
        defaultTime="00:00"
      />
    ),
    endsAt: ({ field }) => (
      <DateTimeField
        value={((field.value ?? '') as string) || undefined}
        onChange={(next) => field.onChange(next ?? '')}
        placeholder={labels?.datePlaceholder}
        disabled={isSubmitting}
        defaultTime="00:00"
      />
    ),

    // --- Priority number input ---
    priority: ({ field }) => (
      <Input
        type="number"
        value={String(field.value ?? 0)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
        disabled={isSubmitting}
      />
    ),
  };

  // -- Field visibility -----------------------------------------------------

  const fieldVisibility = React.useCallback(
    ({
      key,
      values,
    }: {
      key: string;
      values: SchemaValues;
      isEditing: boolean;
    }): boolean => {
      if (key === 'mediaUrl') return values.mediaType !== 'none';
      if (key === 'posterUrl') return values.mediaType === 'video';
      if (key === 'groupIds') return values.targetType === 'groups';
      if (key === 'userIds') return values.targetType === 'users';
      return true;
    },
    [],
  );

  // -- Label mappings -------------------------------------------------------

  const labelMappings: Record<string, string> = {
    title: labels?.title ?? 'Title',
    body: labels?.body ?? 'Body',
    mediaType: labels?.mediaType ?? 'Media type',
    mediaUrl: labels?.mediaUrl ?? 'Media URL',
    posterUrl: labels?.posterUrl ?? 'Poster URL',
    ctaLabel: labels?.ctaLabel ?? 'Button label',
    ctaTo: labels?.ctaTo ?? 'Button link',
    style: labels?.style ?? 'Visual style',
    targetType: labels?.targetType ?? 'Target audience',
    status: labels?.status ?? 'Status',
    skippable: labels?.skippable ?? 'Skippable',
    startsAt: labels?.startsAt ?? 'Starts at',
    endsAt: labels?.endsAt ?? 'Ends at',
    priority: labels?.priority ?? 'Priority',
    groupIds: labels?.groups ?? 'Groups',
    userIds: labels?.users ?? 'Students',
  };

  // -- Render ---------------------------------------------------------------

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
      <DynamicForm
        schema={announcementSchema}
        onSubmit={handleSubmit}
        onClose={onClose}
        defaultValues={dynamicDefaultValues}
        isEditing={!!defaultValues?.title}
        formRef={formRef}
        labelMappings={labelMappings}
        fieldVisibility={fieldVisibility}
        customRenderers={customRenderers as any}
        onValuesChange={setLiveValues}
      />
      <AnnouncementLivePreview values={liveValues} />
    </div>
  );
}
